import {
	ComboBox,
	DeprecatedCreativeModal as CreativeModal,
	DeprecatedScrollbar as Scrollbar,
	FlexColumn,
	FlexRow,
	Label,
	RadioGroupHandler,
	SearchField,
	SubHeading,
	Text,
} from '@forecast-it/design-system';
import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components/macro';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import DirectApi from '../../../directApi';
import InlineLoader from '../../../forecast-app/shared/components/inline-loader/inline_loader';
import {hideLoader, showLoader} from '../../global_loader';
import Util from '../../../forecast-app/shared/util/util';
import ImportSageIntacctProjectMutation from '../../../mutations/ImportSageIntacctProjectMutation';
import {HorizontalBar} from './SageIntacctModal.styled';

const scrollSectionHeight = window?.innerHeight < 900 ? '200px' : '500px';
const ListItem = styled.div`
	width: 550px;
	padding: 18px 8px 18px 8px;
	border-bottom: 1px solid #ccc;
`;

const ListWrapper = styled.div`
	height: ${scrollSectionHeight};
`;

type SageIntacctSyncProjectModalProps = {
	closeModal: () => void;
	forecastProjectId: number;
	forecastProjectName: string;
};

type SageIntacctLocation = {
	LOCATIONID: string;
	NAME: string;
	RECORDNO: number;
};

type SageIntacctProject = {
	RECORDNO: string;
	PROJECTID: string;
	NAME: string;
	LOCATIONID: string;
	DESCRIPTION: string;
	BEGINDATE: string;
	ENDDATE: string;
	STATUS: string;
	CUSTOMERID: string | null;
	BUDGETAMOUNT: string;
};

const NEW_PROJECT = 'new-project';

const SageIntacctSyncProjectModal = ({
	closeModal,
	forecastProjectId,
	forecastProjectName,
}: SageIntacctSyncProjectModalProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const [locations, setLocations] = useState<SageIntacctLocation[]>([]);
	const [fetching, setFetching] = useState(true);
	const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
	const [selectedProjectId, setSelectedProjectId] = useState<string>('');
	const [projects, setProjects] = useState<SageIntacctProject[]>([]);
	const [searchString, setSearchString] = useState<string | null>(null);
	const [filteredProjects, setFilteredProjects] = useState<SageIntacctProject[]>([]);
	const [failed, setFailed] = useState(false);
	const [importing, setImporting] = useState(false);
	const disableSyncButton =
		(importing || fetching || !selectedLocationId || !selectedProjectId) && selectedProjectId !== NEW_PROJECT;

	useEffect(() => {
		DirectApi.Fetch('sageIntacct/locations').then(res => {
			if (res?.locations) {
				setLocations(res.locations);
				setFetching(false);
			} else if (res?.error || res?.status === 500 || res?.statusCode === 500) {
				setFailed(true);
				setFetching(false);
			} else {
				setLocations([]);
				setFetching(false);
			}
		});
	}, []);

	const filterProjects = () => {
		if (searchString) {
			setFilteredProjects(projects.filter(tm => tm.NAME.toLowerCase().includes(searchString.toLowerCase())));
		} else {
			setFilteredProjects(projects);
		}
	};

	useEffect(() => {
		filterProjects();
	}, [searchString, projects]);

	const onLocationSelect = (locationId: string) => {
		setSelectedLocationId(locationId);
		setFetching(true);
		DirectApi.Fetch(`sageIntacct/projects/${locationId}`).then(res => {
			if (res?.projects) {
				setProjects(res.projects);
				setFetching(false);
			} else if (res?.error || res?.status === 500 || res?.statusCode === 500) {
				setFailed(true);
				setFetching(false);
			} else {
				setLocations([]);
				setFetching(false);
			}
		});
	};

	const onFilterChange = (input: string) => {
		setSearchString(input);
	};

	const onSyncClick = () => {
		if (selectedProjectId === NEW_PROJECT) {
			closeModal();
			showModal({
				type: MODAL_TYPE.SAGE_INTACCT_CREATE_NEW_PROJECT,
				forecastProjectId: forecastProjectId,
				forecastProjectName: forecastProjectName,
			});
		} else {
			setImporting(true);
			showLoader();
			Util.CommitMutation(
				ImportSageIntacctProjectMutation,
				{
					forecastProjectId: forecastProjectId,
					sageIntacctProjectId: selectedProjectId,
				},
				() => {
					setImporting(false);
					hideLoader();
					closeModal();
					showModal({
						type: MODAL_TYPE.SAGE_INTACCT_EXPORT_TASKS,
						forecastProjectId: forecastProjectId,
						sageIntacctProjectId: selectedProjectId,
						isPartOfProjectSyncFlow: true,
					});
				}
			);
		}
	};

	return (
		<CreativeModal
			title={'Sync Project with Sage Intacct'}
			size={'l'}
			closeModal={closeModal}
			creativeButtonText={'Sync Project with Sage Intacct'}
			cancelButtonText={formatMessage({id: 'common.cancel'})}
			onCreateHandler={onSyncClick}
			onCancelHandler={closeModal}
			disabled={disableSyncButton}
			replicateDesignSystem
		>
			<ComboBox
				label="Project Location"
				data-cy="combobox-location"
				defaultItems={locations}
				value={selectedLocationId}
				onChange={onLocationSelect}
				width={ComboBox.WIDTH.FLEX}
				disabled={fetching}
			>
				{(location: SageIntacctLocation) => (
					<ComboBox.Item key={location.LOCATIONID} textValue={location.NAME}>
						<Text>{location.NAME}</Text>
					</ComboBox.Item>
				)}
			</ComboBox>

			{failed && <Text color={'error'}>Something has gone wrong, try again or contact support.</Text>}

			<FlexColumn gap={'xs'}>
				<Label>Project</Label>
				<Text color={Text.color.medium}>Please select the project you wish to sync with Sage Intacct</Text>
				<SearchField
					isExpandable={false}
					onChange={onFilterChange}
					value={searchString}
					width="600px"
					isDisabled={!selectedLocationId}
				/>
			</FlexColumn>

			<RadioGroupHandler onValueChange={setSelectedProjectId} value={selectedProjectId}>
				<ListItem>
					<FlexRow gap={'xl'}>
						<RadioGroupHandler.Item value={NEW_PROJECT} />
						<SubHeading size={'2'}>{'New project'}</SubHeading>
					</FlexRow>
				</ListItem>
				<ListWrapper>
					{fetching ? (
						<InlineLoader />
					) : (
						<Scrollbar maxHeight={scrollSectionHeight}>
							<FlexColumn alignItems="flex-start" justifyContent="flex-start">
								{filteredProjects &&
									filteredProjects.map(proj => {
										return (
											<ListItem key={'project-' + proj.PROJECTID}>
												<FlexRow gap={'xl'}>
													<RadioGroupHandler.Item value={proj.PROJECTID} />
													<Text size={'2'}>{proj.NAME}</Text>
												</FlexRow>
											</ListItem>
										);
									})}
							</FlexColumn>
						</Scrollbar>
					)}
				</ListWrapper>
			</RadioGroupHandler>
			<HorizontalBar />
		</CreativeModal>
	);
};

export default SageIntacctSyncProjectModal;
